"use client"
import { useState, useEffect, useMemo, useCallback } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEdit, faTrash, faArrowsUpDownLeftRight ,faArrowsLeftRight} from "@fortawesome/free-solid-svg-icons"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import Tabs from "./modules/Tabs/Tabs"
import Button from "./modules/Button/Button"
import SearchBar from "../shared-modules/SearchBar"
import ProgressBar from "../shared-modules/ProgressBar"
import Table from "./modules/table/table"
import StatusSelector from "./detail/StatusSelector"
import Select from "./modules/Select/Select"
import PendingRefurbishmentSection from "./sections/PendingRefurbishmentSection"
import ReturnDeviceForm from "./modal/ReturnDeviceForm"
import {
  getWarehouseBoxes,
  getIndividualDevices,
  getDeviceTypes,
  getBoxDeviceTypes,
  updateWarehouseDevice,
  updateWarehouseBox,
  deleteWarehouseDevice,
  deleteWarehouseBox,
  fetchByUrl,
} from "../../services/inventory/detail"
import styles from "../../pages/inventory/InventoryDashboardPage.module.css"
import { parseDateSafe } from "../../utils/dateFormatters"
function useDebounced(value, delay = 500) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}
const callServiceWithPage = async (serviceFn, page, filters = {}) => {
  const params = { page, ...filters }
  try {
    const res = await serviceFn(params)
    if (!res) return { results: [], meta: null, raw: null }
    if (res.data) {
      if (Array.isArray(res.data)) return { results: res.data, meta: null, raw: res }
      if (res.data.results || res.data.meta)
        return { results: res.data.results || [], meta: res.data.meta || null, raw: res }
    }
    if (res.results || res.meta) return { results: res.results || [], meta: res.meta || null, raw: res }
    if (Array.isArray(res)) return { results: res, meta: null, raw: res }
    return { results: [], meta: null, raw: res }
  } catch (err) {
    console.debug("callServiceWithPage error:", err?.message || err)
    return { results: [], meta: null, raw: null }
  }
}
const parsePageFromUrl = (url) => {
  if (!url || typeof url !== "string") return null
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.origin : undefined)
    const p = u.searchParams.get("page") ?? u.searchParams.get("p")
    if (p != null) {
      const n = Number(p)
      return Number.isFinite(n) ? n : null
    }
  } catch (err) {
    const m = String(url).match(/[?&](?:page|p)=(\d+)/)
    if (m && m[1]) {
      const n = Number(m[1])
      return Number.isFinite(n) ? n : null
    }
  }
  return null
}
const InventorySection = ({
  statusFilterOptions,
  locationOptions,
  isAdmin,
  onMoveDeviceModal,
  onDeviceDetailsModal,
  onConfirmationModal,
  onInventoryUpdate,
  refreshTrigger,
}) => {
  const [isAdminState, setIsAdminState] = useState(Boolean(isAdmin))
  useEffect(() => {
    try {
      const accountStr = typeof window !== "undefined" ? sessionStorage.getItem("inventoryActiveAccount") : null
      if (accountStr) {
        const account = JSON.parse(accountStr)
        setIsAdminState(account?.type?.toLowerCase()?.trim() === "admin")
      } else {
        setIsAdminState(Boolean(isAdmin))
      }
    } catch (err) {
      console.warn("Failed to read inventoryActiveAccount from sessionStorage:", err)
      setIsAdminState(Boolean(isAdmin))
    }
  }, [isAdmin])
  const [moveSelectedLoading, setMoveSelectedLoading] = useState(false)
  const [moveAllLoading, setMoveAllLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [paginationLoading, setPaginationLoading] = useState(false)
  const [deviceTypes, setDeviceTypes] = useState([])
  const [boxDeviceTypes, setBoxDeviceTypes] = useState([])
  const [inventoryTab, setInventoryTab] = useState("bulk")
  const [selectedParentBoxes, setSelectedParentBoxes] = useState([])
  const [selectedIndividualDevices, setSelectedIndividualDevices] = useState([])
  const [selectedChildDevices, setSelectedChildDevices] = useState({})
  const [deviceSearchTerm, setDeviceSearchTerm] = useState("")
  const [deviceSelectedLocation, setDeviceSelectedLocation] = useState("all")
  const [deviceSelectedStatusFilter, setDeviceSelectedStatusFilter] = useState("all")
  const [bulkSearchTerm, setBulkSearchTerm] = useState("")
  const [bulkSelectedLocation, setBulkSelectedLocation] = useState("all")
  const [bulkSelectedStatusFilter, setBulkSelectedStatusFilter] = useState("all")
  const [openDropdownId, setOpenDropdownId] = useState(null)
  const debouncedDeviceSearch = useDebounced(deviceSearchTerm, 500)
  const debouncedBulkSearch = useDebounced(bulkSearchTerm, 500)
  const [boxesPage, setBoxesPage] = useState(1)
  const [devicesPage, setDevicesPage] = useState(1)
  const queryClient = useQueryClient()
  const [returnModalOpen, setReturnModalOpen] = useState(false)
  const [returnModalDevices, setReturnModalDevices] = useState([])
  const fetchPageByUrl = useCallback(
    async (url, type) => {
      if (!url) {
        setPaginationLoading(false)
        return
      }
      try {
        let urlToFetch = String(url)
        const boxesFilterActive =
          (bulkSelectedStatusFilter && bulkSelectedStatusFilter !== "all") ||
          (bulkSelectedLocation && bulkSelectedLocation !== "all") ||
          (debouncedBulkSearch && String(debouncedBulkSearch).trim() !== "")
        const devicesFilterActive =
          (deviceSelectedStatusFilter && deviceSelectedStatusFilter !== "all") ||
          (deviceSelectedLocation && deviceSelectedLocation !== "all") ||
          (debouncedDeviceSearch && String(debouncedDeviceSearch).trim() !== "")
        let json = await fetchByUrl(urlToFetch)
        if (json) {
          const meta = json.meta || (json.data && json.data.meta) || null
          const results = Array.isArray(json.results)
            ? json.results
            : Array.isArray(json.data)
              ? json.data
              : Array.isArray(json)
                ? json
                : []
          const pageFromUrl = parsePageFromUrl(urlToFetch) || (meta && (meta.current_page || meta.page)) || 1
          const lastPage = meta && (meta.last_page || meta.total_pages || meta.pages) ? Number(meta.last_page ?? meta.total_pages ?? meta.pages) : null
          const filterActive = type === "boxes" ? boxesFilterActive : devicesFilterActive
          const resultsEmpty = Array.isArray(results) && results.length === 0
          const pageOutOfRange = lastPage && pageFromUrl && pageFromUrl > lastPage
          if (filterActive && (resultsEmpty || pageOutOfRange) && pageFromUrl !== 1) {
            try {
              const u = new URL(urlToFetch, typeof window !== "undefined" ? window.location.origin : undefined)
              u.searchParams.set("page", "1")
              urlToFetch = u.toString()
            } catch (err) {
              if (urlToFetch.match(/[?&](?:page|p)=/)) urlToFetch = urlToFetch.replace(/([?&](?:page|p)=)\d+/, "$1" + "1")
              else urlToFetch = urlToFetch + (urlToFetch.includes("?") ? "&page=1" : "?page=1")
            }
            const json2 = await fetchByUrl(urlToFetch)
            if (json2) json = json2
          }
        }
        if (!json) {
          setPaginationLoading(false)
          return
        }
        const results = Array.isArray(json.results)
          ? json.results
          : Array.isArray(json.data)
            ? json.data
            : Array.isArray(json)
              ? json
              : []
        const meta = json.meta || (json.data && json.data.meta) || null
        const pageFromMeta = meta?.current_page ? Number(meta.current_page) : null
        const pageFromUrlFinal = parsePageFromUrl(urlToFetch)
        const pageToSet = pageFromMeta || pageFromUrlFinal || 1
        if (type === "boxes") {
          setBoxesPage(Number(pageToSet))
          queryClient.setQueryData(["warehouseBoxes", Number(pageToSet)], { results, meta, raw: json })
        } else if (type === "devices") {
          setDevicesPage(Number(pageToSet))
          queryClient.setQueryData(["individualDevices", Number(pageToSet)], { results, meta, raw: json })
        }
        setPaginationLoading(false)
      } catch (err) {
        console.error("Error fetching pagination URL:", err)
        setPaginationLoading(false)
      }
    },
    [
      queryClient,
      bulkSelectedStatusFilter,
      bulkSelectedLocation,
      debouncedBulkSearch,
      deviceSelectedStatusFilter,
      deviceSelectedLocation,
      debouncedDeviceSearch,
    ],
  )
  const [boxSort, setBoxSort] = useState({ sort_by: null, order: null })
  const [deviceSort, setDeviceSort] = useState({ sort_by: null, order: null })
  const buildBoxFilters = useCallback(() => {
    const params = {}
    if (debouncedBulkSearch && debouncedBulkSearch.trim() !== "") params.box_number = debouncedBulkSearch.trim()
    if (bulkSelectedStatusFilter && bulkSelectedStatusFilter !== "all") params.inventory_status = bulkSelectedStatusFilter
    if (bulkSelectedLocation && bulkSelectedLocation !== "all") params.current_location_id = bulkSelectedLocation
    if (boxSort?.sort_by) params.sort_by = boxSort.sort_by
    if (boxSort?.order) params.order = boxSort.order
    return params
  }, [debouncedBulkSearch, bulkSelectedStatusFilter, bulkSelectedLocation, boxSort])
  const buildDeviceFilters = useCallback(() => {
    const params = {}
    if (debouncedDeviceSearch && debouncedDeviceSearch.trim() !== "") params.serial = debouncedDeviceSearch.trim()
    if (deviceSelectedStatusFilter && deviceSelectedStatusFilter !== "all") params.inventory_status = deviceSelectedStatusFilter
    if (deviceSelectedLocation && deviceSelectedLocation !== "all") params.current_location_id = deviceSelectedLocation
    if (deviceSort?.sort_by) params.sort_by = deviceSort.sort_by
    if (deviceSort?.order) params.order = deviceSort.order
    return params
  }, [debouncedDeviceSearch, deviceSelectedStatusFilter, deviceSelectedLocation, deviceSort])
  useEffect(() => {
    setBoxesPage(1)
  }, [debouncedBulkSearch, bulkSelectedStatusFilter, bulkSelectedLocation, boxSort])
  useEffect(() => {
    setDevicesPage(1)
  }, [debouncedDeviceSearch, deviceSelectedStatusFilter, deviceSelectedLocation, deviceSort])
  const {
    data: boxesPayload = { results: [], meta: null },
    isLoading: boxesLoading,
    isFetching: boxesFetching,
  } = useQuery(
    ["warehouseBoxes", boxesPage, debouncedBulkSearch, bulkSelectedStatusFilter, bulkSelectedLocation, boxSort.sort_by, boxSort.order],
    async () => await callServiceWithPage(getWarehouseBoxes, boxesPage, buildBoxFilters()),
    {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  )
  const {
    data: devicesPayload = { results: [], meta: null },
    isLoading: devicesLoading,
    isFetching: devicesFetching,
  } = useQuery(
    ["individualDevices", devicesPage, debouncedDeviceSearch, deviceSelectedStatusFilter, deviceSelectedLocation, deviceSort.sort_by, deviceSort.order],
    async () => await callServiceWithPage(getIndividualDevices, devicesPage, buildDeviceFilters()),
    {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  )
  const { data: deviceTypesData = [], isLoading: deviceTypesLoading } = useQuery(
    ["deviceTypes"],
    async () => {
      const res = await getDeviceTypes()
      return Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []
    },
    { staleTime: 1000 * 60 * 5, gcTime: 1000 * 60 * 10, refetchOnWindowFocus: false },
  )
  const { data: boxDeviceTypesData = [], isLoading: boxDeviceTypesLoading } = useQuery(
    ["boxDeviceTypes"],
    async () => {
      const res = await getBoxDeviceTypes()
      return Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []
    },
    { staleTime: 1000 * 60 * 5, gcTime: 1000 * 60 * 10, refetchOnWindowFocus: false },
  )
  useEffect(() => {
    setDeviceTypes(deviceTypesData)
  }, [deviceTypesData])
  useEffect(() => {
    setBoxDeviceTypes(boxDeviceTypesData)
  }, [boxDeviceTypesData])
  const updateBoxMutation = useMutation(({ id, payload }) => updateWarehouseBox(id, payload), {
    onMutate: async ({ id, payload }) => {
      try {
        await queryClient.cancelQueries(["warehouseBoxes"])
        const prev = queryClient.getQueryData(["warehouseBoxes", boxesPage]) || { results: [] }
        queryClient.setQueryData(["warehouseBoxes", boxesPage], (old = { results: [] }) => {
          const arr = Array.isArray(old.results) ? old.results : []
          return { ...old, results: arr.map((b) => (b.id === id ? { ...b, ...payload } : b)) }
        })
        return { previous: prev }
      } catch (err) {
        console.error("Error during box mutation onMutate:", err?.message || err)
        throw err
      }
    },
    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["warehouseBoxes", boxesPage], context.previous)
      }
      console.error("Error updating box:", err?.message || err)
    },
    onSettled: () => {
      queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
    },
  })
  const updateDeviceMutation = useMutation(({ id, payload }) => updateWarehouseDevice(id, payload), {
    onMutate: async ({ id, payload }) => {
      try {
        await queryClient.cancelQueries(["individualDevices"])
        const prev = queryClient.getQueryData(["individualDevices", devicesPage]) || { results: [] }
        queryClient.setQueryData(["individualDevices", devicesPage], (old = { results: [] }) => {
          const arr = Array.isArray(old.results) ? old.results : []
          return { ...old, results: arr.map((d) => (d.id === id ? { ...d, ...payload } : d)) }
        })
        return { previous: prev }
      } catch (err) {
        console.error("Error during device mutation onMutate:", err?.message || err)
        throw err
      }
    },
    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["individualDevices", devicesPage], context.previous)
      }
      console.error("Error updating device:", err?.message || err)
    },
    onSettled: () => {
      queryClient.invalidateQueries(["individualDevices"], { exact: false })
    },
  })
  const deleteBoxMutation = useMutation((id) => deleteWarehouseBox(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
    },
    onError: (err) => {
      console.error("Error deleting box:", err?.message || err)
    },
  })
  const deleteDeviceMutation = useMutation((id) => deleteWarehouseDevice(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(["individualDevices"], { exact: false })
    },
    onError: (err) => {
      console.error("Error deleting device:", err?.message || err)
    },
  })
  const handleInventoryUpdate = useCallback(() => {
    try {
      queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
      queryClient.invalidateQueries(["individualDevices"], { exact: false })
      if (typeof onInventoryUpdate === "function") {
        try {
          onInventoryUpdate()
        } catch (err) {
          console.warn("onInventoryUpdate callback error:", err?.message || err)
        }
      }
    } catch (err) {
      console.error("Error during inventory update:", err?.message || err)
    }
  }, [queryClient, onInventoryUpdate])
  const handleParentSelectionChange = useCallback(
    (selectedIds) => {
      setSelectedParentBoxes(selectedIds)
      setSelectedChildDevices((prev) => {
        const newChildSelection = { ...prev }
          ; (boxes || []).forEach((device) => {
            newChildSelection[device.id] = selectedIds.includes(device.id) ? [] : []
          })
        return newChildSelection
      })
    },
    [],
  )
  const handleIndividualDeviceSelectionChange = useCallback((selectedIds) => {
    setSelectedIndividualDevices(selectedIds)
  }, [])
  const handleStatusChange = useCallback(
    async (item, newStatus, isBox = false) => {
      try {
        if (isBox) {
          await updateBoxMutation.mutateAsync({ id: item.id, payload: { inventory_status: newStatus } })
        } else {
          await updateDeviceMutation.mutateAsync({ id: item.id, payload: { inventory_status: newStatus } })
        }
      } catch (error) {
        console.error(" Error updating status:", error?.message || error)
      }
    },
    [updateBoxMutation, updateDeviceMutation],
  )
  const getBoxTypeName = useCallback(
    (boxTypeId) => {
      if (!boxTypeId) return "Unknown Box Type"
      const boxDeviceType = boxDeviceTypes.find((bdt) => bdt?.id === boxTypeId)
      return boxDeviceType?.name || String(boxTypeId)
    },
    [boxDeviceTypes],
  )
  const transformBoxToDevice = useCallback(
  (box) => {
    if (!box) return null

    return {
      id: box.id,
      caseManager: box.added_by_name || "Unknown",
      delivered_by: box.added_by_name || "—",
      delivered_at: box.added_at
        ? parseDateSafe(box.added_at)?.toLocaleDateString() || "—"
        : "—",
      type: getBoxTypeName(box.device_type),
      boxType: getBoxTypeName(box.box_type),
      serialRegNo: box.box_number || "N/A",
      deviceCount: box.quantity || 0,
      quantity: box.quantity || 0,
      location: box.current_location_name || "Warehouse",
      locationId: box.current_location_id || null,
      date: box.added_at
        ? parseDateSafe(box.added_at)?.toLocaleDateString() || "N/A"
        : "N/A",
      status: box.inventory_status || "Unknown",
      current_client: box.current_client || null,
      devices: Array.isArray(box.devices) ? box.devices : [],
      compatible_device_types: box.box_type_compatible || [],
      originalBox: box,
    }
  },
  [getBoxTypeName]
)

  const transformDeviceToIndividual = useCallback((device) => {
  if (!device) return null

  return {
    id: device.id,

    caseManager: device.added_by_name || "Unknown",
    delivered_by: device.added_by_name || "—",
    delivered_at: device.added_at
      ? parseDateSafe(device.added_at)?.toLocaleDateString() || "—"
      : "—",
    type: device.device_type?.name || "Unknown Device Type",
    serialImei: device.serial || "N/A",
    location: device.current_location_name || "Warehouse",
    locationId: device.current_location_id || null,
    date: device.added_at
      ? parseDateSafe(device.added_at)?.toLocaleDateString() || "N/A"
      : "N/A",
    purchase_date: device.purchase_date || "—",
    warranty_expiry: device.warranty_expiry
      ? parseDateSafe(device.warranty_expiry)?.toLocaleDateString() || "—"
      : "—",
    status: device.inventory_status || "Unknown",
    deviceStatus: device.device_status || "Unknown",
    current_client: device.current_client || null,
    box: device.box || null,
    originalDevice: device,
  }
}, [])


  const boxes = boxesPayload?.results || []
  const boxesMeta = boxesPayload?.meta || null
  const individualDevices = devicesPayload?.results || []
  const devicesMeta = devicesPayload?.meta || null
  const transformedBoxes = useMemo(
    () => (boxes || []).filter(Boolean).map(transformBoxToDevice).filter(Boolean),
    [boxes, transformBoxToDevice],
  )
  const transformedIndividualDevices = useMemo(
    () => (individualDevices || []).filter(Boolean).map(transformDeviceToIndividual).filter(Boolean),
    [individualDevices, transformDeviceToIndividual],
  )
  const statusColorMap = {
    "In Warehouse": { color: "#dbeafe", text: "#1e40af", dotColor: "#1e40af" },
    "Yet to be Received": { color: "#fef3c7", text: "#d97706", dotColor: "#d97706" },
    "In Warehouse (Refurbished)": { color: "#dcfce7", text: "#16a34a", dotColor: "#16a34a" },
    "Pending Refurbishment": { color: "#fef3c7", text: "#d97706", dotColor: "#d97706" },
    Distributed: { color: "#d1fae5", text: "#065f46", dotColor: "#065f46" },
    "Pending Creation": { color: "#fee2e2", text: "#dc2626", dotColor: "#dc2626" },
  }
  const filteredIndividualDevices = transformedIndividualDevices
  const filteredBulkDevices = transformedBoxes
  const filteredDevices = inventoryTab === "bulk" ? filteredBulkDevices : filteredIndividualDevices
  const handleRowClick = useCallback(
    (device) => {
      const isIndividualDevice = inventoryTab === "individual"
      onDeviceDetailsModal({
        isOpen: true,
        selectedDevice: device,
        editMode: false,
        isIndividualDevice,
        onDataRefresh: () => handleInventoryUpdate(),
      })
    },
    [inventoryTab, onDeviceDetailsModal, handleInventoryUpdate],
  )
  const handleEditDevice = useCallback(
    (device = null) => {
      const isIndividualDevice = inventoryTab === "individual"
      onDeviceDetailsModal({
        isOpen: true,
        selectedDevice: device,
        editMode: true,
        isIndividualDevice,
        onDataRefresh: () => handleInventoryUpdate(),
      })
    },
    [inventoryTab, onDeviceDetailsModal, handleInventoryUpdate],
  )
  const handleMoveSingleDevice = useCallback(
    (device) => {
      onMoveDeviceModal({
        isOpen: true,
        selectedDevices: [device],
        isBulkMove: false,
        onSuccess: handleInventoryUpdate,
      })
    },
    [onMoveDeviceModal, handleInventoryUpdate],
  )
  const openReturnModalForDevice = useCallback((device) => {
    const movedAt = new Date().toISOString()
    const locationId = device?.originalDevice?.current_location_id || device?.originalDevice?.location_id || null
    const payloadDevice = {
      id: device.id,
      original: device.originalDevice || device,
      device_ids: [device.originalDevice?.id || device.id],
      moved_at: movedAt,
      location_id: locationId,
    }
    setReturnModalDevices([payloadDevice])
    setReturnModalOpen(true)
  }, [])
  const handleMoveSelectedDevices = useCallback(async () => {
    const selectedIds = inventoryTab === "bulk" ? selectedParentBoxes : selectedIndividualDevices
    const deviceList = inventoryTab === "bulk" ? transformedBoxes : transformedIndividualDevices
    if (selectedIds.length === 0) {
      alert("Please select devices to move")
      return
    }
    setMoveSelectedLoading(true)
    try {
      const selectedDeviceItems = deviceList.filter((device) => selectedIds.includes(device.id))
      await onMoveDeviceModal({
        isOpen: true,
        selectedDevices: selectedDeviceItems,
        isBulkMove: false,
        onSuccess: handleInventoryUpdate,
      })
    } catch (err) {
      console.error("Error during move selected devices:", err?.message || err)
    } finally {
      setMoveSelectedLoading(false)
    }
  }, [
    inventoryTab,
    selectedParentBoxes,
    selectedIndividualDevices,
    transformedBoxes,
    transformedIndividualDevices,
    onMoveDeviceModal,
    handleInventoryUpdate,
  ])
  const handleMoveAllDevices = useCallback(async () => {
    setMoveAllLoading(true)
    try {
      await onMoveDeviceModal({
        isOpen: true,
        selectedDevices: filteredDevices,
        isBulkMove: true,
        onSuccess: handleInventoryUpdate,
      })
    } catch (err) {
      console.error("Error during move all devices:", err?.message || err)
    } finally {
      setMoveAllLoading(false)
    }
  }, [filteredDevices, onMoveDeviceModal, handleInventoryUpdate])
  const handleDeleteSelectedDevices = useCallback(async () => {
    const selectedIds = inventoryTab === "bulk" ? selectedParentBoxes : selectedIndividualDevices
    if (selectedIds.length === 0) return
    const itemType = inventoryTab === "bulk" ? "boxes" : "devices"
    onConfirmationModal({
      isOpen: true,
      title: `Delete Selected ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`,
      message: `Are you sure you want to delete ${selectedIds.length} selected ${itemType}? This action cannot be undone.`,
      onConfirm: async () => {
        setDeleteLoading(true)
        try {
          const deletePromises = selectedIds.map(async (id) => {
            if (inventoryTab === "bulk") return deleteBoxMutation.mutateAsync(id)
            return deleteDeviceMutation.mutateAsync(id)
          })
          await Promise.all(deletePromises)
          queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
          queryClient.invalidateQueries(["individualDevices"], { exact: false })
          if (inventoryTab === "bulk") setSelectedParentBoxes([])
          else setSelectedIndividualDevices([])
        } catch (error) {
          console.error(" Error during bulk delete:", error?.message || error)
        } finally {
          setDeleteLoading(false)
          onConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: null })
        }
      },
      confirmText: "Delete All",
      variant: "danger",
    })
  }, [
    inventoryTab,
    selectedParentBoxes,
    selectedIndividualDevices,
    deleteBoxMutation,
    deleteDeviceMutation,
    queryClient,
    onConfirmationModal,
  ])
  const handleDeleteSingleDevice = useCallback(
    async (device) => {
      const itemType = inventoryTab === "bulk" ? "box" : "device"
      const itemName = device.box_id || device.device_id || device.id
      onConfirmationModal({
        isOpen: true,
        title: `Delete ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}`,
        message: `Are you sure you want to delete ${itemType} "${itemName}"? This action cannot be undone.`,
        onConfirm: async () => {
          setDeleteLoading(true)
          try {
            if (inventoryTab === "bulk") await deleteBoxMutation.mutateAsync(device.id)
            else await deleteDeviceMutation.mutateAsync(device.id)
            queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
            queryClient.invalidateQueries(["individualDevices"], { exact: false })
          } catch (error) {
            console.error(` Error deleting ${itemType}:`, error?.message || error)
          } finally {
            setDeleteLoading(false)
            onConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: null })
          }
        },
        confirmText: "Delete",
        variant: "danger",
      })
    },
    [inventoryTab, deleteBoxMutation, deleteDeviceMutation, queryClient, onConfirmationModal],
  )
  const StatsHeader = ({ warehouseCount = 1500, readyToShipCount = 800 }) => (
    <div className={styles.stats_header}>
      <div className={styles.stat_item}>
        <div className={styles.stat_content}>
          <span className={styles.stat_number}>{warehouseCount}</span>
          <span className={styles.stat_label}>Devices At Warehouse</span>
        </div>
        <ProgressBar percentage={75} color="#3b82f6" showPercentage={false} />
      </div>
      <div className={styles.stat_item}>
        <div className={styles.stat_content}>
          <span className={styles.stat_number}>{readyToShipCount}</span>
          <span className={styles.stat_label}>Devices ready to be shipped</span>
        </div>
        <ProgressBar percentage={60} color="#10b981" showPercentage={false} />
      </div>
    </div>
  )
  const BulkActions = ({ selectedCount, totalCount, onMoveSelected, onMoveAll }) => {
    if (!isAdminState) return null
    return (
      <div className={styles.bulk_actions_group}>
        <Button
          variant="secondary"
          size="small"
          onClick={onMoveSelected}
          disabled={selectedCount === 0 || moveSelectedLoading}
          loading={moveSelectedLoading}
        >
          Move Selected ({selectedCount})
        </Button>
      </div>
    )
  }
  const MOVE_ALLOWED_STATUSES = new Set(["In Warehouse", "In Warehouse (Refurbished)"])
  const EDIT_DISABLE_STATUSES = new Set(["Distributed"])
  const handleOpenStatusDropdown = useCallback((id) => {
    setOpenDropdownId((prev) => (prev === id ? null : id))
  }, [])
  const ActionButtons = ({ item, onEdit, onMove }) => {
    const canMove = MOVE_ALLOWED_STATUSES.has(item?.status)
    const canEdit = !EDIT_DISABLE_STATUSES.has(item?.status)
    const showReturnForDistributed = inventoryTab === "individual" && String(item?.status).trim() === "Distributed"
    return (
      <div className={styles.action_buttons}>
        <button
          className={canEdit ? styles.action_btn : styles.action_btndisable}
          onClick={(e) => {
            e.stopPropagation()
            if (!canEdit) return
            onEdit(item)
          }}
          title={canEdit ? "Edit" : "Edit (disabled - Distributed)"}
          disabled={!canEdit}
          aria-label="Edit"
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
        {showReturnForDistributed ? (
          <button
            className={styles.action_btn}
            onClick={(e) => {
              e.stopPropagation()
              openReturnModalForDevice(item)
            }}
            title="Return"
            aria-label="Return"
          >
            <FontAwesomeIcon icon={faArrowsLeftRight} />
          </button>
        ) : canMove ? (
          <button
            className={styles.action_btn}
            onClick={(e) => {
              e.stopPropagation()
              onMove(item)
            }}
            title="Move"
            aria-label="Move"
          >
            <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />
          </button>
        ) : (
          <button className={styles.action_btndisable} title="Move" disabled aria-label="Move disabled">
            <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />
          </button>
        )}
        <button
          className={styles.action_btn}
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteSingleDevice(item)
          }}
          title="Delete"
          aria-label="Delete"
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>
    )
  }
  const bulkDevicesColumns = [
    { key: "id", label: "Toca Box", alignLeft: true, sorting: true, sortField: "created_at" },
    { key: "caseManager", label: "Case Manager", alignLeft: true, sorting: true },
    { key: "type", label: "Box Type", alignLeft: true, sorting: true },
    { key: "location", label: "Location", alignLeft: true, sorting: true },
    { key: "serialRegNo", label: "Tenovi Box ", alignLeft: true, sorting: true },
    {
      key: "quantity",
      label: "Quantity",
      sorting: true,
      alignLeft: true,
      render: (device) => <span className={styles.device_count}>{device.quantity}</span>,
    },
    {
      key: "status",
      label: "Status",
      sorting: false,
      alignLeft: true,
      render: (box) => {
        const currentStatus = box.status || box.inventory_status
        return (
          <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <StatusSelector
              id={`bulk-${box.id}`}
              currentStatus={currentStatus}
              statusColorMap={statusColorMap}
              onStatusChange={(newStatus) => handleStatusChange(box, newStatus, true)}
              openDropdownId={openDropdownId}
              setOpenDropdownId={handleOpenStatusDropdown}
            />
          </div>
        )
      },
    },
    {
      key: "actions",
      label: "Actions",
      sorting: false,
      render: (device) => <ActionButtons item={device} onEdit={handleEditDevice} onMove={handleMoveSingleDevice} />,
    },
  ]
  const individualDevicesColumns = [
    { key: "caseManager", label: "Case Manager", alignLeft: true, sorting: true },
    { key: "type", label: "Device Type", alignLeft: true, sorting: true },
    { key: "serialImei", label: "Serial/IMEI", alignLeft: true, sorting: true },
    { key: "date", label: "Date", alignLeft: true, sorting: true, sortField: "created_at" },
    { key: "location", label: "Location", alignLeft: true, sorting: true },
    {
      key: "status",
      label: "Status",
      sorting: false,
      alignLeft: true,
      render: (device) => {
        const currentStatus = device.status
        return (
          <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <StatusSelector
              id={`individual-${device.id}`}
              currentStatus={currentStatus}
              statusColorMap={statusColorMap}
              onStatusChange={(newStatus) => handleStatusChange(device, newStatus, false)}
              openDropdownId={openDropdownId}
              setOpenDropdownId={handleOpenStatusDropdown}
            />
          </div>
        )
      },
    },
    {
      key: "actions",
      label: "Actions",
      sorting: false,
      render: (device) => <ActionButtons item={device} onEdit={handleEditDevice} onMove={handleMoveSingleDevice} />,
    },
  ]
  const inventoryTabs = [
    { id: "bulk", label: "Bulk" },
    { id: "individual", label: "Individual" },
    { id: "pending", label: "Pending Refurbishment" },
  ]
  const getCurrentDeviceColumns = () => {
    const cols = inventoryTab === "bulk" ? [...bulkDevicesColumns] : [...individualDevicesColumns]
    if (!isAdminState && inventoryTab === "individual") {
      const returnCol = {
        key: "return",
        label: "Return",
        sorting: false,
        render: (device) => (
          <div>
            <Button
              variant="ghost"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                openReturnModalForDevice(device)
              }}
            >
              <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />
            </Button>
          </div>
        ),
      }
      cols.push(returnCol)
      return cols.filter((c) => c.key !== "actions")
    }
    if (!isAdminState) return cols.filter((c) => c.key !== "actions")
    return cols
  }
  const getCurrentSelectedDevices = () => (inventoryTab === "bulk" ? selectedParentBoxes : selectedIndividualDevices)
  const getCurrentDeviceSelectionHandler = () =>
    inventoryTab === "bulk" ? handleParentSelectionChange : handleIndividualDeviceSelectionChange
  const onBoxPageChange = useCallback(
    (pageOrUrl) => {
      setPaginationLoading(true)
      if (typeof pageOrUrl === "string") {
        fetchPageByUrl(pageOrUrl, "boxes")
      } else if (typeof pageOrUrl === "number") {
        setBoxesPage(pageOrUrl)
      } else {
        setPaginationLoading(false)
      }
    },
    [fetchPageByUrl],
  )
  const onDevicePageChange = useCallback(
    (pageOrUrl) => {
      setPaginationLoading(true)
      if (typeof pageOrUrl === "string") {
        fetchPageByUrl(pageOrUrl, "devices")
      } else if (typeof pageOrUrl === "number") {
        setDevicesPage(pageOrUrl)
      } else {
        setPaginationLoading(false)
      }
    },
    [fetchPageByUrl],
  )
  useEffect(() => {
    if (refreshTrigger) {
      queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
      queryClient.invalidateQueries(["individualDevices"], { exact: false })
    }
  }, [refreshTrigger])
  useEffect(() => {
    queryClient.invalidateQueries(["warehouseBoxes"], { exact: false })
    queryClient.invalidateQueries(["individualDevices"], { exact: false })
    queryClient.invalidateQueries(["deviceTypes"], { exact: false })
    queryClient.invalidateQueries(["boxDeviceTypes"], { exact: false })
  }, [])
  const loading = boxesLoading || devicesLoading || deviceTypesLoading || boxDeviceTypesLoading
  return (
    <div className={styles.inventory_content}>
      <StatsHeader />
      <div className={styles.tabs_container}>
        <Tabs
          tabs={inventoryTabs}
          activeTab={inventoryTab}
          onChange={(tab) => setInventoryTab(tab)}
          variant="secondary"
          size="small"
        />
      </div>
      {inventoryTab === "pending" ? (
        <div style={{ marginTop: 12 }}>
          <PendingRefurbishmentSection
            onComplete={(device) => {
              handleInventoryUpdate()
            }}
          />
        </div>
      ) : (
        <>
          <div className={styles.search_section}>
            {inventoryTab === "bulk" ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <SearchBar
                  key="bulk-search"
                  value={bulkSearchTerm}
                  onSearchChange={setBulkSearchTerm}
                  placeholder="Search by box id"
                  maxWidth="300px"
                />
                <Select
                  key="bulk-status-select"
                  options={statusFilterOptions}
                  value={bulkSelectedStatusFilter}
                  onChange={setBulkSelectedStatusFilter}
                  placeholder="Status Filter"
                  size="small"
                />
                {!isAdminState && (
                  <Select
                    key="bulk-location-select"
                    options={locationOptions}
                    value={bulkSelectedLocation}
                    onChange={setBulkSelectedLocation}
                    placeholder="Select Location"
                    size="small"
                  />
                )}
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <SearchBar
                  key="device-search"
                  value={deviceSearchTerm}
                  onSearchChange={setDeviceSearchTerm}
                  placeholder="Search by serial no"
                  maxWidth="300px"
                />
                <Select
                  key="device-status-select"
                  options={statusFilterOptions}
                  value={deviceSelectedStatusFilter}
                  onChange={setDeviceSelectedStatusFilter}
                  placeholder="Status Filter"
                  size="small"
                />
                {!isAdminState && (
                  <Select
                    key="device-location-select"
                    options={locationOptions}
                    value={deviceSelectedLocation}
                    onChange={setDeviceSelectedLocation}
                    placeholder="Select Location"
                    size="small"
                  />
                )}
              </div>
            )}
          </div>
          <div className={styles.table_container}>
            <Table
              columns={getCurrentDeviceColumns()}
              data={filteredDevices}
              isRowClickable={true}
              onRowClick={handleRowClick}
              loading={loading}
              showCheckboxes={isAdminState}
              onSelectionChange={getCurrentDeviceSelectionHandler()}
              selectedIds={getCurrentSelectedDevices()}
              key={`${inventoryTab}-${filteredDevices.length}-${inventoryTab === "bulk" ? boxesMeta?.current_page || boxesPage : devicesMeta?.current_page || devicesPage}`}
              pagination={inventoryTab === "bulk" ? !!boxesMeta : !!devicesMeta}
              meta={inventoryTab === "bulk" ? boxesMeta : devicesMeta}
              onPageChange={inventoryTab === "bulk" ? onBoxPageChange : onDevicePageChange}
              paginationLoading={paginationLoading}
              onSortChange={(sort_by, order) => {
                if (inventoryTab === "bulk") {
                  setBoxSort({ sort_by: sort_by ?? null, order: order ?? null })
                  setBoxesPage(1)
                } else {
                  setDeviceSort({ sort_by: sort_by ?? null, order: order ?? null })
                  setDevicesPage(1)
                }
              }}
            />
          </div>
          <div className={styles.bulk_actions_section}>
            <BulkActions
              selectedCount={getCurrentSelectedDevices().length}
              totalCount={filteredDevices.length}
              onMoveSelected={handleMoveSelectedDevices}
              onMoveAll={handleMoveAllDevices}
            />
          </div>
        </>
      )}
      {}
      <ReturnDeviceForm
        isOpen={returnModalOpen}
        devices={returnModalDevices}
        onClose={() => setReturnModalOpen(false)}
        onSuccess={() => {
          setReturnModalOpen(false)
          handleInventoryUpdate()
        }}
      />
    </div>
  )
}
export default InventorySection
